// Operations dashboard — the console's landing page. Fleet KPIs straight from
// the control plane: tenant lifecycle counts, signups over 30/90 days,
// contracts pipeline, pilots about to lapse with no signed contract, recent
// impersonation grants, and provisioning failures that need a human.

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getMetricsOverview } from "../lib/api";
import { useApi } from "../lib/useApi";
import { formatDate, formatDateShort } from "../lib/format";
import type { MetricsOverviewResponse, SignupPoint } from "../lib/types";
import { TopBar } from "../components/Layout";
import { EmptyState, ErrorState, Loading } from "../components/States";

export function DashboardPage() {
  const { data, loading, error, reload } = useApi((signal) => getMetricsOverview(signal), []);

  return (
    <>
      <TopBar
        crumbs="Operations"
        actions={
          <button className="btn" onClick={reload} disabled={loading}>
            Refresh
          </button>
        }
      />
      <div className="content">
        <div className="page-head">
          <div>
            <h1>Operations</h1>
            <div className="sub">
              Fleet health at a glance — tenants, signups, contracts and support access.
            </div>
          </div>
        </div>

        {loading ? (
          <Loading label="Loading fleet metrics…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : data ? (
          <Dashboard data={data} />
        ) : null}
      </div>
    </>
  );
}

function Dashboard({ data }: { data: MetricsOverviewResponse }) {
  const t = data.tenants_by_status;
  const c = data.contracts_by_status;
  const awaiting = (c.sent ?? 0) + (c.viewed ?? 0);

  return (
    <>
      <div className="kpi-grid">
        <Kpi label="Active tenants" value={t.active} to="/tenants?status=active" tone="ok" />
        <Kpi label="Provisioning" value={t.provisioning} to="/tenants?status=provisioning" />
        <Kpi label="Suspended" value={t.suspended} to="/tenants?status=suspended" tone={t.suspended > 0 ? "warn" : undefined} />
        <Kpi label="Failed" value={t.failed} to="/tenants?status=failed" tone={t.failed > 0 ? "danger" : undefined} />
        <Kpi label="Awaiting signature" value={awaiting} to="/contracts" />
        <Kpi label="Signed contracts" value={c.signed ?? 0} to="/contracts" tone="ok" />
      </div>

      <div className="grid-2">
        <SignupsCard signups={data.signups} />
        <ExpiringTrialsCard data={data} />
      </div>

      <div className="grid-2">
        <ProvisioningAttentionCard data={data} />
        <RecentImpersonationCard data={data} />
      </div>
    </>
  );
}

function Kpi({
  label,
  value,
  to,
  tone,
}: {
  label: string;
  value: number;
  to: string;
  tone?: "ok" | "warn" | "danger" | undefined;
}) {
  return (
    <Link to={to} className={`kpi${tone ? ` ${tone}` : ""}`}>
      <span className="kpi-value">{value}</span>
      <span className="kpi-label">{label}</span>
    </Link>
  );
}

/** Zero-fill the per-day signup series over the last `days` days (UTC). */
function fillSeries(signups: SignupPoint[], days: number): SignupPoint[] {
  const byDay = new Map(signups.map((p) => [p.day, p.n]));
  const out: SignupPoint[] = [];
  const today = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - i));
    const key = d.toISOString().slice(0, 10);
    out.push({ day: key, n: byDay.get(key) ?? 0 });
  }
  return out;
}

function SignupsCard({ signups }: { signups: SignupPoint[] }) {
  const [days, setDays] = useState<30 | 90>(30);
  const series = useMemo(() => fillSeries(signups, days), [signups, days]);
  const total = series.reduce((sum, p) => sum + p.n, 0);
  const max = Math.max(1, ...series.map((p) => p.n));

  return (
    <div className="card">
      <div className="card-head">
        <h2>Signups</h2>
        <div className="chips" role="group" aria-label="Signup window">
          {([30, 90] as const).map((d) => (
            <button
              key={d}
              type="button"
              className={`chip${days === d ? " active" : ""}`}
              onClick={() => setDays(d)}
              aria-pressed={days === d}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>
      <div className="card-body">
        <div className="kpi-inline">
          <strong>{total}</strong> new tenant{total === 1 ? "" : "s"} in the last {days} days
        </div>
        {total === 0 ? (
          <EmptyState>No signups in this window.</EmptyState>
        ) : (
          <>
            <div className="spark" role="img" aria-label={`Daily signups, last ${days} days`}>
              {series.map((p) => (
                <div
                  key={p.day}
                  className="bar"
                  style={{ height: `${Math.max(3, Math.round((p.n / max) * 100))}%`, opacity: p.n === 0 ? 0.18 : undefined }}
                  title={`${formatDateShort(Date.parse(`${p.day}T00:00:00Z`) / 1000)}: ${p.n}`}
                />
              ))}
            </div>
            <div className="spark-axis">
              <span>{series[0]?.day}</span>
              <span>{series[series.length - 1]?.day}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function daysLeft(unixSeconds: number): number {
  return Math.max(0, Math.ceil((unixSeconds - Date.now() / 1000) / 86400));
}

function ExpiringTrialsCard({ data }: { data: MetricsOverviewResponse }) {
  const trials = data.expiring_trials;
  return (
    <div className="card">
      <div className="card-head">
        <h2>Pilots ending soon</h2>
        <span className="pill">{trials.length}</span>
      </div>
      <div className="card-body tight">
        {trials.length === 0 ? (
          <EmptyState>
            No pilot windows ending in the next 30 days without a signed contract.
          </EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Trial ends</th>
                <th>Left</th>
              </tr>
            </thead>
            <tbody>
              {trials.map((tr) => {
                const left = daysLeft(tr.trial_ends_at);
                return (
                  <tr key={tr.tenant_id}>
                    <td>
                      <Link to={`/tenants/${tr.tenant_id}`}>
                        <strong>{tr.name}</strong>
                      </Link>{" "}
                      <span className="mono faint">{tr.slug}</span>
                    </td>
                    <td className="num">{formatDateShort(tr.trial_ends_at)}</td>
                    <td>
                      <span className={`pill${left <= 7 ? " urgent" : ""}`}>
                        {left} day{left === 1 ? "" : "s"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function ProvisioningAttentionCard({ data }: { data: MetricsOverviewResponse }) {
  const failures = data.recent_failures;
  const stuck = data.stuck_provisioning;
  const clean = failures.length === 0 && stuck.length === 0;
  return (
    <div className="card">
      <div className="card-head">
        <h2>Provisioning attention</h2>
        <span className="pill mono">
          {data.jobs_by_status.queued} queued · {data.jobs_by_status.running} running
        </span>
      </div>
      <div className="card-body tight">
        {clean ? (
          <EmptyState>No failed jobs and nothing stuck. All clear.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Issue</th>
                <th>Tenant</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {stuck.map((s) => (
                <tr key={`stuck-${s.tenant_id}`}>
                  <td>
                    <span className="badge status-suspended">stuck</span>
                  </td>
                  <td>
                    <Link to={`/tenants/${s.tenant_id}`} className="mono">
                      {s.slug}
                    </Link>
                  </td>
                  <td className="faint">in provisioning for {s.minutes_stuck} min</td>
                </tr>
              ))}
              {failures.map((f) => (
                <tr key={f.job_id}>
                  <td>
                    <span className="badge status-failed">{f.kind} failed</span>
                  </td>
                  <td>
                    <Link to={`/tenants/${f.tenant_id}`} className="mono">
                      {f.tenant_id}
                    </Link>
                  </td>
                  <td className="faint" title={f.last_error ?? undefined}>
                    {f.last_error ? truncate(f.last_error, 80) : formatDate(f.updated_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function RecentImpersonationCard({ data }: { data: MetricsOverviewResponse }) {
  const grants = data.recent_impersonation;
  const now = Math.floor(Date.now() / 1000);
  return (
    <div className="card">
      <div className="card-head">
        <h2>Recent impersonation</h2>
      </div>
      <div className="card-body tight">
        {grants.length === 0 ? (
          <EmptyState>No impersonation grants issued yet.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Staff</th>
                <th>Reason</th>
                <th>When</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {grants.map((g) => {
                const live = !g.revoked_at && g.expires_at > now;
                return (
                  <tr key={g.id}>
                    <td>
                      <Link to={`/tenants/${g.tenant_id}`}>{g.name}</Link>
                    </td>
                    <td className="mono">{g.staff_email ?? <span className="faint">—</span>}</td>
                    <td title={g.reason}>{truncate(g.reason, 48)}</td>
                    <td className="num" style={{ whiteSpace: "nowrap" }}>
                      {formatDate(g.created_at)}
                    </td>
                    <td>
                      <span className={`pill${live ? " ok" : ""}`}>
                        {g.revoked_at ? "Revoked" : live ? "Active" : "Expired"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}
